import {
  Pill,
  AlertTriangle,
  Stethoscope,
  Activity,
  Accessibility,
  Eye,
  Ear,
  MessageSquare,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface PatientMedicalInfoProps {
  patient: {
    current_medications: string | null;
    allergies: string | null;
    chronic_conditions: string | null;
    primary_care_physician: string | null;
    pcp_phone: string | null;
    mobility_aids: string[] | null;
    hearing_devices: string[] | null;
    vision_devices: string[] | null;
    communication_devices: string[] | null;
    other_assistive_devices: string | null;
    accessibility_needs: string | null;
  };
  vitals: {
    systolic_bp: number | null;
    diastolic_bp: number | null;
    heart_rate: number | null;
    blood_glucose: number | null;
    oxygen_saturation: number | null;
    recorded_at: string;
    bp_status: string | null;
    heart_rate_status: string | null;
    glucose_status: string | null;
    spo2_status: string | null;
  } | null;
}

export function PatientMedicalInfo({ patient, vitals }: PatientMedicalInfoProps) {
  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "normal":
        return "bg-green-100 text-green-800";
      case "elevated":
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "high":
      case "critical":
        return "bg-red-100 text-red-800";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Current Vitals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Latest Vitals
          </CardTitle>
          {vitals && (
            <CardDescription>
              Recorded {new Date(vitals.recorded_at).toLocaleDateString()}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {vitals ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Blood Pressure</p>
                <p className="text-xl font-bold">
                  {vitals.systolic_bp}/{vitals.diastolic_bp}
                </p>
                <Badge className={getStatusColor(vitals.bp_status)} variant="secondary">
                  {vitals.bp_status || "Unknown"}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Heart Rate</p>
                <p className="text-xl font-bold">{vitals.heart_rate} bpm</p>
                <Badge className={getStatusColor(vitals.heart_rate_status)} variant="secondary">
                  {vitals.heart_rate_status || "Unknown"}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Blood Glucose</p>
                <p className="text-xl font-bold">{vitals.blood_glucose || "—"} mg/dL</p>
                <Badge className={getStatusColor(vitals.glucose_status)} variant="secondary">
                  {vitals.glucose_status || "Unknown"}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Oxygen Saturation</p>
                <p className="text-xl font-bold">{vitals.oxygen_saturation || "—"}%</p>
                <Badge className={getStatusColor(vitals.spo2_status)} variant="secondary">
                  {vitals.spo2_status || "Unknown"}
                </Badge>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-6">
              No vitals recorded yet
            </p>
          )}
        </CardContent>
      </Card>

      {/* Primary Care */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Primary Care Physician
          </CardTitle>
        </CardHeader>
        <CardContent>
          {patient.primary_care_physician ? (
            <div className="space-y-2">
              <p className="font-medium">{patient.primary_care_physician}</p>
              {patient.pcp_phone && (
                <p className="text-muted-foreground">{patient.pcp_phone}</p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">Not specified</p>
          )}
        </CardContent>
      </Card>

      {/* Medications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5" />
            Current Medications
          </CardTitle>
        </CardHeader>
        <CardContent>
          {patient.current_medications ? (
            <p className="whitespace-pre-wrap">{patient.current_medications}</p>
          ) : (
            <p className="text-muted-foreground">No medications listed</p>
          )}
        </CardContent>
      </Card>

      {/* Allergies */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Allergies
          </CardTitle>
        </CardHeader>
        <CardContent>
          {patient.allergies ? (
            <div className="flex flex-wrap gap-2">
              {patient.allergies.split(",").map((allergy, i) => (
                <Badge key={i} variant="destructive">
                  {allergy.trim()}
                </Badge>
              ))}
            </div>
          ) : (
            <Badge variant="outline">No known allergies</Badge>
          )}
        </CardContent>
      </Card>

      {/* Chronic Conditions */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Chronic Conditions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {patient.chronic_conditions ? (
            <p className="whitespace-pre-wrap">{patient.chronic_conditions}</p>
          ) : (
            <p className="text-muted-foreground">No chronic conditions listed</p>
          )}
        </CardContent>
      </Card>

      {/* Assistive Devices & Accessibility */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Accessibility className="h-5 w-5" />
            Accessibility & Assistive Devices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                <Accessibility className="h-4 w-4" />
                Mobility Aids
              </p>
              {patient.mobility_aids && patient.mobility_aids.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {patient.mobility_aids.map((aid, i) => (
                    <Badge key={i} variant="secondary">{aid}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">None</p>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                <Ear className="h-4 w-4" />
                Hearing Devices
              </p>
              {patient.hearing_devices && patient.hearing_devices.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {patient.hearing_devices.map((device, i) => (
                    <Badge key={i} variant="secondary">{device}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">None</p>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                <Eye className="h-4 w-4" />
                Vision Devices
              </p>
              {patient.vision_devices && patient.vision_devices.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {patient.vision_devices.map((device, i) => (
                    <Badge key={i} variant="secondary">{device}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">None</p>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                <MessageSquare className="h-4 w-4" />
                Communication Devices
              </p>
              {patient.communication_devices && patient.communication_devices.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {patient.communication_devices.map((device, i) => (
                    <Badge key={i} variant="secondary">{device}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">None</p>
              )}
            </div>
          </div>
          {patient.accessibility_needs && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">Additional Accessibility Needs</p>
              <p>{patient.accessibility_needs}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
